import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CardContent,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import TableCompo from "../Table/TableCompo";
import { useDispatch, useSelector } from "react-redux";
import { fetchJewellerReportRequest } from "../../../redux/JewellerList/JewellerReportSlice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { apiRequest } from "../../../redux/Reports/reportsSlice";
import useCsvConverter from "../../../hooks/convertToCSV";

export default function Reports() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const dispatch = useDispatch();
  const { convertToCsv } = useCsvConverter();
  const { data: reportResponse } = useSelector(
    (state) => state.jewellerReport || {},
  );

  let package_name = useSelector(
    (state) => state?.appConfig?.configData.package_name || "",
  );
  let package_name_short = package_name.split(".")[2];
  // if login as 

  const decimal = useSelector(
        (state) => state?.appConfig?.configData?.decimalPrecision || 4,
      );

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(5);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const columns = [
    { field: "jewellerName", headerName: "Jeweller Name" },
    { field: "state", headerName: "State" },
    { field: "city", headerName: "City" },
    { field: "owner_name", headerName: "Owner Name" },
    { field: "owner_number", headerName: "Owner Number" },
    { field: "ios_app_status", headerName: "iOS App Status" },
    { field: "android_app_status", headerName: "Android App Status" },
    {
      field: "grace_period_days_for_jeweller",
      headerName: "Grace Period Days (Jeweller)",
    },
    {
      field: "grace_period_days_for_customer",
      headerName: "Grace Period Days (Customer)",
    },
    { field: "payment_gateways", headerName: "Payment Gateways" },
    { field: "first_download_date", headerName: "First Download Date" },
    { field: "tenth_registration_date", headerName: "Tenth Registration Date" },
    {
      field: "fiftieth_registration_date",
      headerName: "Fiftieth Registration Date",
    },
    { field: "first_transaction_date", headerName: "First Transaction Date" },
    { field: "tenth_transaction_date", headerName: "Tenth Transaction Date" },
    {
      field: "fiftieth_transaction_date",
      headerName: "Fiftieth Transaction Date",
    },
    { field: "total_registrations", headerName: "Total Registrations" },
    { field: "total_loggin_users", headerName: "Total Logged-in Users" },
    { field: "total_sip_created", headerName: "Total SIP Created" },
    { field: "sip_installment_paid", headerName: "SIP Installments Paid" },
    { field: "sell_amt", headerName: "Sell Amount" },
    { field: "sell_weight", headerName: "Sell Weight" },
    { field: "buy_amt", headerName: "Buy Amount" },
    { field: "buy_weight", headerName: "Buy Weight" },
    { field: "redeem_amt", headerName: "Redeem Amount" },
    { field: "redeem_weight", headerName: "Redeem Weight" },
    { field: "weight_utilized", headerName: "Weight Utilized" },
    { field: "gift_sent", headerName: "Gift Sent" },
    { field: "gift_received", headerName: "Gift Received" },
    { field: "transactions", headerName: "Total Transactions" },
    {
      field: "auto_debit_feature_status",
      headerName: "SIP Auto Debit Feature Status",
    },
    {
      field: "lastNotificationSentDate",
      headerName: "Last Notification Sent Date",
    },
    { field: "lastRateUpdatedDate", headerName: "Last Rate Updated Date" },
    { field: "liveRate_status", headerName: "LiveRate Status" },
    { field: "trainer_name", headerName: "Training Given By" },
    { field: "trainingStatus", headerName: "Training Status" },
    { field: "trainingScheduleDate", headerName: "Training Schedule Date" },
    { field: "trainingCompleteDate", headerName: "Training Completed Date" },
    { field: "training_description", headerName: "Comment" },
    { field: "subscription_start_date", headerName: "Subscription Start Date" },
    { field: "subscription_end_date", headerName: "Subscription End Date" },
    {
      field: "subscription_plan_amount",
      headerName: "Subscription Plan Amount",
    },
    {
      field: "subscription_discount_amount",
      headerName: "Subscription Discount Amount",
    },
    {
      field: "subscription_payable_amount",
      headerName: "Subscription Package Amount",
    },
    {
      field: "subscription_payable_with_gst",
      headerName: "Subscription Package With GST",
    },
    { field: "subscription_total_paid", headerName: "Subscription Total Paid" },
    {
      field: "subscription_total_pending",
      headerName: "Subscription Total Pending",
    },
  ];

  const fetchReports = () => {
    if (startDate > endDate) {
      toast.error("Start Date cannot be after End Date");
      return;
    }
    setLoading(true);

    const payload = {
      start: page * limit,
      limit,
      search: search.trim(),
    };

    if (startDate && endDate) {
      payload.startDate = startDate.toISOString();
      payload.endDate = endDate.toISOString();
    }

    dispatch(fetchJewellerReportRequest(payload));
    setLoading(false);
  };

  // Keep date range on pagination/search (fetchReports reads startDate/endDate from state)
  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchReports closes over latest dates; avoid listing unstable fn
  }, [page, limit, search, startDate, endDate]);

  const handleTableChange = (type, params) => {
    if (type === "changePage") setPage(params.page);
    if (type === "changeRowsPerPage") {
      setLimit(params.rowsPerPage);
      setPage(0);
    }
    if (type === "search") {
      setSearch(params.searchText.trim());
      setPage(0);
    }
  };

  const tableData = reportResponse?.data?.data
    ? reportResponse.data.data.map((row, index) => {
        const formatValue = (value, isDate = false) => {
          if (value === null || value === undefined || value === "")
            return "N.A";
          if (isDate) {
            const date = new Date(value);
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = date.getFullYear();
            let hours = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, "0");
            const ampm = hours >= 12 ? "PM" : "AM";
            hours = hours % 12;
            hours = hours ? hours : 12;
            hours = String(hours).padStart(2, "0");
            return `${day}-${month}-${year}, ${hours}:${minutes} ${ampm}`;
          }
          return value;
        };

        return {
          id: index + 1,
          jewellerName: formatValue(row.jewellerName),
          state: formatValue(row.state),
          city: formatValue(row.city),
          owner_name: formatValue(row.owner_name),
          owner_number: formatValue(row.owner_number),
          first_download_date: formatValue(row.first_download_date, true),
          tenth_registration_date: formatValue(
            row.tenth_registration_date,
            true,
          ),
          fiftieth_registration_date: formatValue(
            row.fiftieth_registration_date,
            true,
          ),
          first_transaction_date: formatValue(row.first_transaction_date, true),
          tenth_transaction_date: formatValue(row.tenth_transaction_date, true),
          fiftieth_transaction_date: formatValue(
            row.fiftieth_transaction_date,
            true,
          ),
          auto_debit_feature_status: formatValue(row.auto_debit_feature_status),
          total_registrations: formatValue(row.total_registrations),
          total_loggin_users: formatValue(row.total_loggin_users),
          total_sip_created: formatValue(row.total_sip_created),
          sip_installment_paid: formatValue(row.sip_installment_paid),
          sell_amt:
            row.sell_amt !== null &&
            row.sell_amt !== undefined &&
            row.sell_amt !== ""
              ? Number(row.sell_amt).toFixed(2)
              : "N.A",
          sell_weight: Number(row.sell_weight).toFixed(decimal),
          buy_amt: Number(row.buy_amt).toFixed(2),
          buy_weight: Number(row.buy_weight).toFixed(decimal),
          redeem_amt: Number(row.redeem_amt).toFixed(2),
          redeem_weight: Number(row.redeem_weight).toFixed(decimal),
          weight_utilized: Number(row.weight_utilized).toFixed(decimal),
          gift_sent: formatValue(row.gift_sent),
          gift_received: formatValue(row.gift_received),
          transactions: formatValue(row.transactions),
          lastNotificationSentDate: formatValue(
            row.lastNotificationSentDate,
            true,
          ),
          lastRateUpdatedDate: formatValue(row.lastRateUpdatedDate, true),
          trainingStatus: formatValue(row.training_status),
          trainingScheduleDate: formatValue(row.training_schedule_date, true),
          trainingCompleteDate: formatValue(row.training_complete_date, true),
          liveRate_status: formatValue(row.liveRate_status),
          grace_period_days_for_jeweller: formatValue(
            row.grace_period_days_for_jeweller,
          ),
          grace_period_days_for_customer: formatValue(
            row.grace_period_days_for_customer,
          ),
          ios_app_status: row.jewellerAppStatus?.ios === true ? "true" : "false",
          android_app_status:
            row.jewellerAppStatus?.android === true ? "true" : "false",
          training_description: formatValue(row.training_description),
          trainer_name: formatValue(row.trainer_name),
          payment_gateways: formatValue(row.payment_gateways),
          subscription_start_date: formatValue(
            row.subscription_start_date,
            true,
          ),
          subscription_end_date: formatValue(row.subscription_end_date, true),
          subscription_plan_amount: formatValue(row.subscription_plan_amount.toFixed(2)),
          subscription_discount_amount: formatValue(
            row.subscription_discount_amount.toFixed(2),
          ),
          subscription_payable_amount: formatValue(
            row.subscription_payable_amount.toFixed(2),
          ),
          subscription_payable_with_gst: formatValue(
            row.subscription_payable_with_gst.toFixed(2),
          ),
          subscription_total_paid: formatValue(row.subscription_total_paid.toFixed(2)),
          subscription_total_pending: formatValue(
            row.subscription_total_pending.toFixed(2),
          ),
          subscription_tds: formatValue(row.subscription_tds.toFixed(2)),
        };
      })
    : [];

  const totalCount = reportResponse?.data?.total || 0;

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadReportCsv = () => {
    console.log("Store CSV API called");

    dispatch(
      apiRequest({
        method: "GET",
        endpoint: "/api/v1/jeweller/reportCsvData",
        query: {},
        callback: (response) => {
          console.log("Store CSV response:", response);

          const csv = convertToCsv(response?.data || []);
          triggerCsvDownload(csv, "report.csv");
        },
      }),
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          width: "100%",
          mr: 2,
          mt: 0,
          mb: { xs: 2, md: 2 },
          background: "#fff",
          borderRadius: "3px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          p: 2,
        }}
      >
        <Box mt={3}>
          <Typography
            fontWeight={700}
            fontSize={24}
            mb={2}
            fontFamily={"Aeonik"}
          >
            Report
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box
              display="flex"
              flexDirection={isMobile ? "column" : "row"}
              gap={2}
              mb={2}
            >
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                format="dd/MM/yyyy"
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                format="dd/MM/yyyy"
                onChange={setEndDate}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
              <Button
                variant="contained"
                fullWidth={isMobile}
                sx={{
                  backgroundColor: "#7e22ce",
                  whiteSpace: "nowrap",
                  mt: isMobile ? 1 : 0,
                }}
                onClick={fetchReports}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          </LocalizationProvider>
        </Box>

        <CardContent sx={{ width: "100%" }}>
          <TableCompo
            columns={columns}
            data={tableData}
            page={page}
            limit={limit}
            totalCount={totalCount}
            onTableChange={handleTableChange}
            extraButtons={
              (tableData?.length === 1) ? (
                <Button
                  size="sm"
                  variant="outlined"
                  onClick={handleDownloadReportCsv}
                >
                  Download CSV
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outlined"
                  component="a"
                  href={`${process.env.REACT_APP_API_BASE_URL}/static_data/${package_name_short}_reports.csv?nocache=${Date.now()}`}
                  download="report.csv"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download CSV
                </Button>
              )
            }
          />
        </CardContent>
      </Box>
    </Box>
  );
}
